import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Loading from '../../../common/components/Loading/Loading';
import CommandJobs from './CommandJobs';
import Store from '../../../redux/Store';
import Constants from '../../../common/constants/Constants';
import DeploymentStateEnum from '../../../common/constants/DeploymentStateEnum';
import { showSnackbar } from '../../../common/state/snackbar/Snackbar.actions';
import { getCommandJobs } from './CommandJobActions';
import { withRouter } from '../../../common/utils/NavigationWrapper';

function CommandJobsContainer(props) {
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);

  const [columnConfigs, setColumnConfigs] = useState([
    {
      name: 'Package name',
      key: 'Name',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Description',
      key: 'Description',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Type',
      key: 'Type',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Version',
      key: 'Version',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Instruments',
      key: 'AmountInstruments',
      type: 'number',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Target instrument type',
      key: 'InstrumentType',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Job start date',
      key: 'JobStartDate',
      type: 'datetime',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Notified',
      key: 'Notified',
      type: 'number',
      visible: false,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Transfer started',
      key: 'TransferStarted',
      type: 'number',
      visible: false,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Transfer completed',
      key: 'TransferCompleted',
      type: 'number',
      visible: false,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Execution started',
      key: 'ExecutionStarted',
      type: 'number',
      visible: false,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Execution completed',
      key: 'ExecutionCompleted',
      type: 'number',
      visible: false,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Reported failures',
      key: 'AmountFailedCommands',
      type: 'number',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Job finish date',
      key: 'JobFinishDate',
      type: 'datetime',
      visible: true,
      filterable: true,
      sortable: true,
    },
  ]);

  useEffect(() => {
    getCommandJobs()
      .then((res) => {
        setData(res.body);
        setIsFetching(false);
      })
      .catch((err) => {
        setIsFetching(false);
        Store.dispatch(
          showSnackbar({
            type: Constants.SNACKBAR_MESSAGE_TYPES.ERROR,
            message: 'Failed to load deployment jobs.',
          })
        );
      });
  }, []);

  useEffect(() => {
    if (props && props.packageDeploymentMessage) {
      _handlePackageDeploymentMessage(props.packageDeploymentMessage);
    }
  }, [props.packageDeploymentMessage]);

  const _handlePackageDeploymentMessage = (message) => {
    const {
      CommandGuid,
      DeploymentState,
      Error,
      ErrorMessage,
      DeploymentStateTime,
      JobFinishTime,
    } = JSON.parse(message.data);

    let copiedState = [...data];

    const commandJobIndex = copiedState.findIndex((job) =>
      job.PackageDeployments.find((x) => x.CommandGuid == CommandGuid)
    );

    if (commandJobIndex === -1) {
      // ignore the message in case the Command job isn't loaded
      return;
    }

    let deploymentIndex = copiedState[commandJobIndex].PackageDeployments.findIndex(
      (x) => x.CommandGuid == CommandGuid
    );

    if (typeof JobFinishTime == 'undefined') {
      copiedState[commandJobIndex].FinishTime = null;
    } else {
      copiedState[commandJobIndex].FinishTime = JobFinishTime;
    }

    copiedState[commandJobIndex].PackageDeployments[deploymentIndex] = {
      ...copiedState[commandJobIndex].PackageDeployments[deploymentIndex],
      State: DeploymentState,
      Error: Error,
      ErrorMessage: ErrorMessage,
      StateTime: DeploymentStateTime,
    };
    setData(copiedState);
  };

  const _adjustProperties = (data) => {
    let adjustedCommandJobs = [];

    data.forEach((commandJob) => {
      let newJob = {};

      if (commandJob.PackageDeployments[0] != undefined) {
        newJob.PackageName = commandJob.PackageDeploymentJobId;
        newJob.Name = commandJob.Name;
        newJob.Description = commandJob.PackageDeployments[0].Package.Description;
        newJob.Type = commandJob.PackageDeployments[0].Package.Type;
        newJob.Version = commandJob.PackageDeployments[0].Package.Version;
        newJob.AmountInstruments = commandJob.PackageDeployments.length;
        newJob.InstrumentType = commandJob.PackageDeployments[0].Package.InstrumentType;
        newJob.JobStartDate = commandJob.StartTime;
        newJob.Notified = _count(
          commandJob.PackageDeployments,
          'State',
          DeploymentStateEnum.FILE_AVAILABLE_MESSAGE_SENT
        );
        newJob.TransferStarted = _count(
          commandJob.PackageDeployments,
          'State',
          DeploymentStateEnum.TRANSFER_STARTED
        );
        newJob.TransferCompleted = _count(
          commandJob.PackageDeployments,
          'State',
          DeploymentStateEnum.TRANSFER_COMPLETED
        );
        newJob.ExecutionStarted = _count(
          commandJob.PackageDeployments,
          'State',
          DeploymentStateEnum.EXECUTION_STARTED
        );
        newJob.ExecutionCompleted = _count(
          commandJob.PackageDeployments,
          'State',
          DeploymentStateEnum.EXECUTION_COMPLETED
        );
        newJob.AmountFailedCommands = _count(commandJob.PackageDeployments, 'Error', true);
        newJob.JobFinishDate = commandJob.FinishTime;

        adjustedCommandJobs.push(newJob);
      }
    });

    return adjustedCommandJobs;
  };

  const _count = (dataset, objValue, compareValue) => {
    const count = dataset.reduce((counter, obj) => {
      if (obj[objValue] === compareValue) counter += 1;
      return counter;
    }, 0);

    return count;
  };

  const _onClickRowCallback = (el) => {
    const { navigate } = props;
    navigate(`/command-job/${el.PackageName}`);
  };

  const _onClickNewCommandJob = () => {
    const { navigate } = props;
    navigate(`/command-job-creator/`);
  };

  return (
    <Loading condition={isFetching}>
      <div>
        <CommandJobs
          data={_adjustProperties(data)}
          columnConfigs={columnConfigs}
          onClickRow={_onClickRowCallback}
          newCommandJob={_onClickNewCommandJob}
        />
      </div>
    </Loading>
  );
}

const mapStateToProps = (state) => ({
  packageDeploymentMessage:
    state.push.pushMessages != null
      ? state.push.pushMessages.find((el) => el.type === 'PackageDeploymentFeedback')
      : null,
});

export default withRouter(connect(mapStateToProps)(CommandJobsContainer, 'CommandJob'));
